import Button from "@material-ui/core/Button/Button";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection, stopPropagation } from "@saleor/misc";
import { PaymentEvents_paymentEvents_edges_node } from "@saleor/paymentEvents/types/PaymentEvents";
import { filterApi, getTimestamp } from "@saleor/paymentEvents/util";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      acquirementId: {
        overflow: "hidden",
        paddingRight: 20,
        whiteSpace: "nowrap",
        width: 180
      },
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "25em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      eventType: {
        overflow: "hidden",
        paddingRight: 20,
        whiteSpace: "nowrap",
        width: 180
      },
      table: {
        tableLayout: "fixed"
      }
    };
  },
  { name: "PaymentEventsList" }
);

export interface PaymentEventsListProps extends ListProps {
  paymentEvents: PaymentEvents_paymentEvents_edges_node[];
  onView: (api: string, id: string) => void;
}

export const PaymentEventsList: React.FC<PaymentEventsListProps> = ({
  onView,
  paymentEvents,
  onNextPage,
  onPreviousPage,
  onRowClick,
  onUpdateListSettings,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 2;
  const classes = useStyles({});
  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead colSpan={colNum} disabled={disabled} items={[]}>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Order Code" />
          </TableCellHeader>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Customer Name" />
          </TableCellHeader>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Transaction ID" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Reference" />
          </TableCellHeader>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Amount" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Timestamp" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Event Type" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Acquirement ID" />
          </TableCellHeader>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="API Request" />
          </TableCellHeader>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="API Response" />
          </TableCellHeader>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {paymentEvents &&
            renderCollection(
              paymentEvents,
              (paymentEvent, index) => {
                const { apiRequest, apiResponse } = filterApi(
                  paymentEvent.parameters
                );
                return (
                  <TableRow
                    hover={!!paymentEvent}
                    key={paymentEvent ? index : "skeleton"}
                    onClick={
                      paymentEvent
                        ? onRowClick(
                            btoa(`StoreOrder:${atob(paymentEvent.id)}`)
                          )
                        : undefined
                    }
                  >
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => paymentEvent.order_code,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => paymentEvent.customer_name,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => paymentEvent.transaction_id,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => paymentEvent.smop_trans_id,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => paymentEvent.amount,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => getTimestamp(paymentEvent.created_at),
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.eventType}>
                      {maybe<React.ReactNode>(
                        () => paymentEvent.event_type,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.acquirementId}>
                      {maybe<React.ReactNode>(
                        () => paymentEvent.acquirement_id,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {apiRequest === null ? (
                        <Skeleton />
                      ) : (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={stopPropagation(() =>
                            onView("request", paymentEvent.id)
                          )}
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {apiResponse === null ? (
                        <Skeleton />
                      ) : (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={stopPropagation(() =>
                            onView("response", paymentEvent.id)
                          )}
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No Result Found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default PaymentEventsList;
